/**
 * The page displaying the history of tasks.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2024-2026
 */
import '@/components/styles/Input.css'
import TabContainer from '@/components/TabContainer'
import AimdTaskList from '@/pages/TaskList/AimdTaskList'
import {
  RefreshButtonId,
  RefreshButtonLabel,
} from '@/pages/TaskList/JobListDef'
import TaskList from '@/pages/TaskList/TaskList'
import { useSelector } from 'react-redux'
import './TaskList.css'

import { fetchAimdTaskList, fetchAllTaskList } from '@/api'
import RectRoundButton from '@/components/Button/RectRoundButton'
import HorizontalProgressBar from '@/components/HorizontalProgressBar'
import { logger, LogTag } from '@/config/logger'
import { getUserState } from '@/store'
import { useEffect, useRef, useState } from 'react'
import { BiLoaderAlt } from 'react-icons/bi'

export const TaskListPage = () => {
  const selectTabIndex = Number(
    new URLSearchParams(window.location.search).get('selectTab')
  )

  const { aimdTaskList, lastAimdTaskId, jobList, lastJobId } =
    useSelector(getUserState)
  const [refreshing, setRefreshing] = useState(false)

  // Counts how many lists are still pending after a refresh. Set to 2
  // on click (one per list); each list's useEffect decrements it when
  // its data arrives in the Redux store. `refreshing` is cleared only
  // when both reach 0 — not when the fetch promises resolve, because
  // those resolve before the store is updated.
  const pendingRefreshCount = useRef(0)

  useEffect(() => {
    fetchAimdTaskList({ refreshing: true })
    fetchAllTaskList({ refreshing: true })
  }, [])

  useEffect(() => {
    logger.debug(LogTag.TaskList, 'All task list:', jobList)
    if (pendingRefreshCount.current > 0) {
      pendingRefreshCount.current--
      if (pendingRefreshCount.current === 0) setRefreshing(false)
    }
  }, [jobList])

  useEffect(() => {
    logger.debug(LogTag.TaskList, 'AIMD task list:', aimdTaskList)
    if (pendingRefreshCount.current > 0) {
      pendingRefreshCount.current--
      if (pendingRefreshCount.current === 0) setRefreshing(false)
    }
  }, [aimdTaskList])

  // NOTE: setRefreshing(false) is intentionally NOT called here after
  // the fetches. These functions return before the Redux store is
  // updated, so calling setRefreshing(false) right after would clear
  // the loading state too early — React batches both state changes
  // into one render and the button never shows "Loading". Instead,
  // the useEffects above reset it once both lists have actually
  // changed in the store.
  const onRefreshClick = () => {
    if (!refreshing) {
      pendingRefreshCount.current = 2
      setRefreshing(true)
      fetchAimdTaskList({ refreshing: true })
      fetchAllTaskList({ refreshing: true })
    }
  }

  return (
    <div>
      {!aimdTaskList || !jobList ? (
        <HorizontalProgressBar />
      ) : (
        <div className="jobui-globalContainer page">
          <TabContainer
            tabList={['AI Music Detector', 'All Tasks']}
            customComp={
              <RectRoundButton
                id={RefreshButtonId}
                onClick={onRefreshClick}
                icon={
                  refreshing ? (
                    <BiLoaderAlt className="loadingIcon" />
                  ) : undefined
                }
                label={refreshing ? 'Loading' : RefreshButtonLabel}
              />
            }
            selectTabIndex={selectTabIndex}
          >
            <AimdTaskList
              taskList={aimdTaskList?.value ?? []}
              lastTaskId={lastAimdTaskId}
              requestMoreTasks={() => fetchAimdTaskList({ refreshing: false })}
            />
            <TaskList
              taskList={jobList?.value ?? []}
              lastTaskId={lastJobId}
              requestMoreTasks={() => fetchAllTaskList({ refreshing: false })}
            />
          </TabContainer>
        </div>
      )}
    </div>
  )
}
